import type { RoadmapItem, RoadmapResponse } from "../types";

type SeedEpic = Pick<RoadmapItem, "title" | "summary" | "category"> & { stories: string[] };

const seedEpics: SeedEpic[] = [
  {
    title: "Evidence-container expansion",
    summary: "Broaden acquisition and input formats beyond the initial RAW and EnCase 6 E01 contract.",
    category: "Acquisition",
    stories: ["Resumable E01 acquisition", "Ex01 support", "L01 support", "AFF4 support", "Broader segmented-image handling"]
  },
  {
    title: "Device discovery coverage",
    summary: "Improve geometry and mount discovery for unusual storage paths.",
    category: "Acquisition",
    stories: ["Multipath devices", "Virtual disks", "Vendor-specific controllers", "Unusual geometry reporting"]
  },
  {
    title: "BitLocker recovery expansion",
    summary: "Add recovery methods that can be implemented without weakening evidence handling.",
    category: "Encryption",
    stories: ["Passphrase protectors", "Standalone BEK recovery keys", "Clear and paused states", "Additional protector research", "Damaged metadata recovery"]
  },
  {
    title: "Windows storage support",
    summary: "Understand modern Windows storage layouts that are not supported by the initial TSK path.",
    category: "Filesystems",
    stories: ["ReFS analysis", "Dynamic Disks", "Storage Spaces"]
  },
  {
    title: "RAID and filesystem recovery",
    summary: "Reconstruct multi-disk layouts and recover from damaged filesystem metadata.",
    category: "Filesystems",
    stories: ["Software RAID", "Hardware RAID metadata", "Damaged-filesystem recovery"]
  },
  {
    title: "Partition coverage",
    summary: "Expand partition traversal beyond primary MBR entries.",
    category: "Filesystems",
    stories: ["Extended MBR partition chains"]
  },
  {
    title: "Windows artifact expansion",
    summary: "Cover more artifact types, format versions, and recovery states.",
    category: "Artifacts",
    stories: ["Additional artifact families", "Broader version support", "Richer parser confidence reporting"]
  },
  {
    title: "Fragmented carving",
    summary: "Move beyond contiguous signatures while retaining bounded and explainable results.",
    category: "Carving",
    stories: ["Fragment reconstruction", "Fragment confidence reporting"]
  },
  {
    title: "Threat-intelligence integration",
    summary: "Expand the bounded local indicator support into interoperable intelligence workflows.",
    category: "Threat intelligence",
    stories: ["Full STIX pattern evaluation", "TAXII client"]
  },
  {
    title: "Collaborative cases",
    summary: "Support coordinated analysis without weakening locking or audit guarantees.",
    category: "Cases",
    stories: ["Network-safe locking", "Cloud-synchronized cases", "Multi-user workflows"]
  },
  {
    title: "Reporting and provenance",
    summary: "Create richer analyst narratives and optional external integrity signals.",
    category: "Reporting",
    stories: ["Analyst narrative reports", "Trusted timestamps", "External notarization"]
  },
  {
    title: "Platform portability",
    summary: "Make experimental builds easier to install on more host architectures.",
    category: "Distribution",
    stories: ["Self-contained Linux worker", "Self-contained macOS worker", "Windows ARM64", "Additional Linux and macOS architectures"]
  }
];

let nextIssue = 1;
const items: RoadmapItem[] = [];
for (const epic of seedEpics) {
  const epicNumber = nextIssue++;
  items.push({
    product: "blockr",
    issueNumber: epicNumber,
    issueUrl: `https://blockr.mashr.ai/roadmap#item-${epicNumber}`,
    type: "epic",
    title: epic.title,
    summary: epic.summary,
    category: epic.category,
    status: "under_consideration",
    verifiedVoteCount: 0,
    currentUserVoted: false,
    completedChildren: 0,
    totalChildren: epic.stories.length,
    updatedAt: "2026-08-09T00:00:00Z"
  });
  for (const title of epic.stories) {
    const storyNumber = nextIssue++;
    items.push({
      product: "blockr",
      issueNumber: storyNumber,
      issueUrl: `https://blockr.mashr.ai/roadmap#item-${storyNumber}`,
      type: "user_story",
      parentIssueNumber: epicNumber,
      title,
      summary: `Community-requested work within ${epic.title.toLowerCase()}.`,
      category: epic.category,
      status: "under_consideration",
      verifiedVoteCount: 0,
      currentUserVoted: false,
      completedChildren: 0,
      totalChildren: 0,
      updatedAt: "2026-08-09T00:00:00Z"
    });
  }
}

export const seedRoadmap: RoadmapResponse = {
  product: {
    slug: "blockr",
    name: "Blockr",
    projectUrl: "https://blockr.mashr.ai/roadmap",
    ideasUrl: "https://blockr.mashr.ai/roadmap#suggest-an-idea"
  },
  items,
  source: "seed",
  syncedAt: "2026-08-09T00:00:00Z"
};

export const qualityCommitments = [
  "Privileged Windows 11 VSS and BitLocker fixture validation",
  "Complete GPT header and entry-array CRC validation",
  "Windows Authenticode and Apple signing/notarization",
  "Security fixes and dependency maintenance",
  "Reproducible builds, SBOMs, and third-party license compliance"
];

export const permanentNonGoals = [
  "Hardware or software write blocking",
  "Malware containment or endpoint remediation",
  "Secret storage or secure deletion",
  "Host attestation",
  "Offline TPM-only unlocking without recovery material",
  "Legal admissibility, accreditation, or organizational chain-of-custody policy"
];
